import React, { createContext, useCallback, useContext, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

/**
 * Async confirmation dialog using shadcn AlertDialog.
 * More reliable than window.confirm in Safari and consistent UI.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   if (await confirm({ title: '...', description: '...' })) { ... }
 */
const ConfirmContext = createContext(null);

export const ConfirmProvider = ({ children }) => {
  const [state, setState] = useState(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      setState({
        title: opts.title || '¿Confirmar?',
        description: opts.description || '',
        confirmText: opts.confirmText || 'Eliminar',
        cancelText: opts.cancelText || 'Cancelar',
        danger: opts.danger !== false,
        resolve,
      });
    });
  }, []);

  const handle = (value) => {
    if (state) state.resolve(value);
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={!!state} onOpenChange={(o) => { if (!o) handle(false); }}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-[#0F2922]">{state?.title}</AlertDialogTitle>
            {state?.description && (
              <AlertDialogDescription className="text-[#5C6C62] whitespace-pre-line">
                {state.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => handle(false)} data-testid="confirm-cancel">
              {state?.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handle(true)}
              data-testid="confirm-ok"
              className={state?.danger ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
            >
              {state?.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
};
